import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { CompanyDetailPanel } from "./CompanyDetailPanel";
import { type FeedbackState } from "./FeedbackButtons";
import type { CompanyResult } from "@/types/api";

// ── Props ────────────────────────────────────────────────────────────────────

interface InlineDetailExpansionProps {
  result: CompanyResult;
  jobId: string;
  expanded: boolean;
  feedbackState?: FeedbackState;
  onFeedback: (
    resultId: string,
    signal: "relevant" | "not_relevant",
    reason?: string,
    note?: string,
  ) => Promise<boolean>;
  onEnriched: (
    resultId: string,
    enrichmentData: Record<string, unknown>,
  ) => void;
  onClose: () => void;
}

// ── Component ────────────────────────────────────────────────────────────────

export function InlineDetailExpansion({
  result,
  jobId,
  expanded,
  feedbackState,
  onFeedback,
  onEnriched,
  onClose,
}: InlineDetailExpansionProps) {
  const { t } = useTranslation("results");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Manage `inert` attribute and focus in a single effect — both depend on
  // `expanded` and must execute in order (remove inert before focusing).
  // React does not natively support `inert` as a prop, so we use a ref.
  useEffect(() => {
    if (wrapperRef.current) {
      if (expanded) {
        wrapperRef.current.removeAttribute("inert");
        // Focus the panel so keyboard users land inside the detail region
        panelRef.current?.focus();
      } else {
        wrapperRef.current.setAttribute("inert", "");
      }
    }
  }, [expanded]);

  // After the CSS grid transition completes, scroll the panel into view so the
  // newly revealed content is visible without the user needing to scroll
  // manually. Using transitionend avoids an arbitrary setTimeout.
  useEffect(() => {
    if (!expanded || !wrapperRef.current) return;
    const el = wrapperRef.current;
    const handler = (e: TransitionEvent) => {
      // Only scroll after the grid expansion transition, not child transitions
      if (e.propertyName !== "grid-template-rows") return;
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    };
    el.addEventListener("transitionend", handler as EventListener, {
      once: true,
    });
    return () =>
      el.removeEventListener("transitionend", handler as EventListener);
  }, [expanded]);

  // Close on Escape key, but only when the panel is expanded and the user is
  // not currently typing in a form field.
  useEffect(() => {
    if (!expanded) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const active = document.activeElement;
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active instanceof HTMLSelectElement
      ) {
        return;
      }
      onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [expanded, onClose]);

  return (
    // The outer ref manages `inert`. When collapsed this wrapper is inert so
    // nothing inside can receive focus or be reached by assistive technology.
    <div ref={wrapperRef}>
      {/* CSS grid animation: gridTemplateRows 0fr → 1fr collapses/expands the
          inner content without needing JS-measured heights. The motion-reduce
          variant respects the user's OS preference. */}
      <div
        className="grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none"
        style={{ gridTemplateRows: expanded ? "1fr" : "0fr" }}
        data-expanded={expanded}
      >
        {/* overflow-hidden on this element is what makes the 0fr trick work —
            the content is present in the DOM but visually clipped to zero. */}
        <div className="overflow-hidden">
          {/* Inner content container with subtle background tint. The relative
              positioning anchors the absolutely positioned close button. */}
          <div className="relative px-6 py-5 bg-muted/40 rounded-lg">
            {/* Close button — absolutely positioned top-right of the content
                region. */}
            <button
              className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-muted transition-colors"
              onClick={onClose}
              aria-label={t("expansion.closePanel")}
            >
              <X className="h-4 w-4" />
            </button>

            {/* Panel container: receives focus when expanded, serves as the
                accessible landmark for the expanded detail region. tabIndex={-1}
                makes it programmatically focusable without adding it to the
                natural tab sequence. */}
            <div
              ref={panelRef}
              role="region"
              aria-label={result.company_name || "Details"}
              tabIndex={-1}
              className="outline-none"
            >
              <CompanyDetailPanel
                result={result}
                jobId={jobId}
                expanded={expanded}
                feedbackState={feedbackState}
                onFeedback={onFeedback}
                onEnriched={onEnriched}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
